import { Component, ErrorInfo, ReactNode } from 'react';
import { jwtAuthService } from '../services/jwtAuthService';
import { AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ error, errorInfo });

    if (error.message.includes('JSON') || error.message.includes('Unexpected token')) {
      localStorage.removeItem('jwt_access_token');
      localStorage.removeItem('jwt_refresh_token');
      localStorage.removeItem('jwt_token_expiry');
      localStorage.removeItem('jwt_user');
      jwtAuthService.logout();

      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  }

  handleReload = () => {
    this.setState({ hasError: false });
    window.location.reload();
  };

  handleClearAuth = () => {
    jwtAuthService.logout().finally(() => {
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
    });
  };

  render() {
    if (this.state.hasError) {
      const isJsonError =
        this.state.error?.message.includes('JSON') ||
        this.state.error?.message.includes('Unexpected token');

      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-12 text-center">
          <AlertTriangle className="h-16 w-16 text-warning-700 mb-6" />
          <h1 className="text-2xl font-semibold mb-2">Something went wrong</h1>
          <p className="text-muted-foreground mb-8 max-w-md">
            {isJsonError
              ? 'Authentication error detected. The page will reload automatically to restore connection.'
              : 'An unexpected error occurred. Please try refreshing the page.'}
          </p>
          <div className="flex gap-3">
            <Button onClick={this.handleReload}>Reload Page</Button>
            <Button variant="outline" onClick={this.handleClearAuth}>
              Clear Session &amp; Login
            </Button>
          </div>

          {import.meta.env.DEV && (
            <details className="mt-8 text-left w-full max-w-2xl">
              <summary className="cursor-pointer text-sm text-muted-foreground">
                Error Details (Development Mode)
              </summary>
              <pre className="mt-2 bg-muted p-4 rounded-lg text-xs overflow-auto max-h-[300px]">
                {this.state.error?.toString()}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
